import { Injectable, OnInit } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  map,
  Observable,
  fromEvent,
  BehaviorSubject,
  combineLatest,
} from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class MediaQueryService implements OnInit {
  isPhone$: Observable<boolean> | undefined;
  isTablet$: Observable<boolean> | undefined;
  isMobile$: Observable<boolean> | undefined;

  resized: BehaviorSubject<boolean> = new BehaviorSubject(false);

  // for script solution
  isDesktop$: Observable<boolean> | undefined;
  isDesktopSm$: Observable<boolean> | undefined;
  isDesktopLg$: Observable<boolean> | undefined;
  isDesktopXl$: Observable<boolean> | undefined;
  isDesktop2Xl$: Observable<boolean> | undefined;
  isDesktop3Xl$: Observable<boolean> | undefined;
  isDesktop4Xl$: Observable<boolean> | undefined;

  /**
   * When true (itinerary detail / fullscreen popup open), layout breakpoints
   * use the host page / live viewport width instead of #imgoingcalendar-wrapper width.
   * Fixes desktop sites that embed the widget in a narrow content column.
   */
  private preferHostWidth$ = new BehaviorSubject<boolean>(false);
  private hostWidth$ = new BehaviorSubject<number | null>(null);

  /**
   * https://github.com/angular/components/blob/main/src/cdk/layout/breakpoints.ts
   * @param Breakpoints
   * export const Breakpoints = {
   XSmall: '(max-width: 599.98px)',
   Small: '(min-width: 600px) and (max-width: 959.98px)',
   Medium: '(min-width: 960px) and (max-width: 1279.98px)',
   Large: '(min-width: 1280px) and (max-width: 1919.98px)',
   XLarge: '(min-width: 1920px)',

   Handset:
   '(max-width: 599.98px) and (orientation: portrait), ' +
   '(max-width: 959.98px) and (orientation: landscape)',
   Tablet:
   '(min-width: 600px) and (max-width: 839.98px) and (orientation: portrait), ' +
   '(min-width: 960px) and (max-width: 1279.98px) and (orientation: landscape)',
   Web:
   '(min-width: 840px) and (orientation: portrait), ' +
   '(min-width: 1280px) and (orientation: landscape)',

   HandsetPortrait: '(max-width: 599.98px) and (orientation: portrait)',
   TabletPortrait: '(min-width: 600px) and (max-width: 839.98px) and (orientation: portrait)',
   WebPortrait: '(min-width: 840px) and (orientation: portrait)',

   HandsetLandscape: '(max-width: 959.98px) and (orientation: landscape)',
   TabletLandscape: '(min-width: 960px) and (max-width: 1279.98px) and (orientation: landscape)',
   WebLandscape: '(min-width: 1280px) and (orientation: landscape)',
   };
   */

  constructor(
    public activatedRoute: ActivatedRoute,
    private breakpointObserver: BreakpointObserver
  ) {
    this.installHostViewportListener();

    const wrapper =
      document.getElementById('imgoingcalendar-wrapper') ||
      document.getElementById('imgoingcalendar-wrapper-wix');
    if (wrapper) {
      const getWrapperWidth = () =>
        wrapper.getBoundingClientRect().width || window.innerWidth;
      console.log('wrapperRect', getWrapperWidth());

      const resize$ = fromEvent(window, 'resize').pipe(
        debounceTime(200),
        map(() => getWrapperWidth())
      );
      const wrapperWidth$ = new BehaviorSubject<number>(getWrapperWidth());
      resize$.subscribe((w) => {
        wrapperWidth$.next(w);
        // While detail is open, keep host layout width on the live viewport so
        // resizing (e.g. 900px → 500px) does not stick on a stale seed value.
        this.refreshPreferHostWidthFromLiveViewport();
      });

      const layoutWidth$ = combineLatest([
        wrapperWidth$,
        this.hostWidth$,
        this.preferHostWidth$,
      ]).pipe(map(([wrapperW, hostW, preferHost]) => this.resolveLayoutWidth(wrapperW, hostW, preferHost)));

      this.isMobile$ = layoutWidth$.pipe(map((w) => w <= 1080));
      this.isDesktop$ = layoutWidth$.pipe(map((w) => w > 1080));
      this.isDesktopSm$ = layoutWidth$.pipe(map((w) => w > 1080 && w <= 1280));
      this.isDesktopLg$ = layoutWidth$.pipe(map((w) => w > 1280 && w <= 1390));
      this.isDesktopXl$ = layoutWidth$.pipe(map((w) => w > 1390 && w <= 1536));
      this.isDesktop2Xl$ = layoutWidth$.pipe(map((w) => w > 1536 && w <= 1799));
      this.isDesktop3Xl$ = layoutWidth$.pipe(map((w) => w > 1799 && w <= 2199));
      this.isDesktop4Xl$ = layoutWidth$.pipe(map((w) => w >= 2200));
      return;
    }

    this.isPhone$ = this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(map(({ matches }) => matches));

    this.isTablet$ = this.breakpointObserver
      .observe([Breakpoints.Tablet])
      .pipe(map(({ matches }) => matches));

    this.isMobile$ = this.breakpointObserver
      .observe(['(max-width: 1080px)'])
      .pipe(map(({ matches }) => matches));

    this.isDesktopSm$ = this.breakpointObserver
      .observe(['(min-width: 1081px) and (max-width: 1280px)'])
      .pipe(map(({ matches }) => matches));

    this.isDesktopLg$ = this.breakpointObserver
      .observe(['(min-width: 1281px) and (max-width: 1390px)'])
      .pipe(map(({ matches }) => matches));

    this.isDesktopXl$ = this.breakpointObserver
      .observe(['(min-width: 1391px) and (max-width: 1536px)'])
      .pipe(map(({ matches }) => matches));

    this.isDesktop2Xl$ = this.breakpointObserver
      .observe(['(min-width: 1537px) and (max-width: 1799px)'])
      .pipe(map(({ matches }) => matches));

    this.isDesktop3Xl$ = this.breakpointObserver
      .observe(['(min-width: 1800px)'])
      .pipe(map(({ matches }) => matches));

    this.isDesktop4Xl$ = this.breakpointObserver
      .observe(['(min-width: 2200px)'])
      .pipe(map(({ matches }) => matches));

    fromEvent(window, 'resize')
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.resized.next(true);
      });
  }

  /**
   * Call when itinerary detail popup opens/closes so layout uses host viewport
   * width (fullscreen) instead of the embed column width.
   */
  public setPreferHostViewportForLayout(prefer: boolean) {
    this.syncHostViewportFromWindowGlobal();
    if (prefer) {
      // Seed immediately so the first isMobile$ emission is not based on a
      // narrow iframe/wrapper width before the parent posts host viewport.
      // Prefer live innerWidth — never screen.width (does not track window resize).
      this.refreshPreferHostWidthFromLiveViewport(true);
    }
    this.preferHostWidth$.next(!!prefer);
    document.documentElement.classList.toggle('ig-itinerary-detail-open', !!prefer);
    try {
      window.dispatchEvent(new Event('resize'));
    } catch (_err) {
      // ignore
    }
  }

  public notifyItineraryDetailOpenedToParent() {
    this.setPreferHostViewportForLayout(true);
    if (window.parent === window) {
      return;
    }
    try {
      window.parent.postMessage({ type: 'imgoing-itinerary-detail-open' }, '*');
      window.parent.postMessage({ type: 'imgoing-scroll-to-widget' }, '*');
    } catch (_err) {
    }
  }

  public notifyItineraryDetailClosedToParent() {
    this.setPreferHostViewportForLayout(false);
    if (window.parent === window) {
      return;
    }
    try {
      window.parent.postMessage({ type: 'imgoing-itinerary-detail-close' }, '*');
    } catch (_err) {
    }
  }

  ngOnInit(): void {}

  private installHostViewportListener() {
    if ((window as any).__igHostViewportListenerInstalled) {
      this.syncHostViewportFromWindowGlobal();
      return;
    }
    (window as any).__igHostViewportListenerInstalled = true;
    this.syncHostViewportFromWindowGlobal();
    window.addEventListener('message', (event: MessageEvent) => {
      const data = event && event.data;
      if (!data || data.type !== 'imgoing-host-viewport') {
        return;
      }
      const width = Number(data.width);
      const height = Number(data.height);
      (window as any).__igHostViewport = {
        width: width > 0 ? width : null,
        height: height > 0 ? height : null,
      };
      if (width > 0) {
        this.hostWidth$.next(Math.round(width));
      }
    });
  }

  private syncHostViewportFromWindowGlobal() {
    const globalViewport = (window as any).__igHostViewport;
    if (!globalViewport) {
      return;
    }
    if (Number.isFinite(globalViewport.width) && globalViewport.width > 0) {
      this.hostWidth$.next(Math.round(globalViewport.width));
    }
  }

  /**
   * Refresh hostWidth$ while prefer-host layout is active (or force-seeding on open).
   * Never use screen.width. Non-iframe: always track live innerWidth on resize.
   * Iframe: trust parent `imgoing-host-viewport` posts (host posts on every resize);
   * do not overwrite those with the iframe content width.
   */
  private refreshPreferHostWidthFromLiveViewport(force = false) {
    if (!force && !this.preferHostWidth$.value) {
      return;
    }
    if (window.parent !== window) {
      this.syncHostViewportFromWindowGlobal();
      const fromParent = (window as any).__igHostViewport;
      if (
        fromParent &&
        Number.isFinite(fromParent.width) &&
        fromParent.width > 0
      ) {
        return;
      }
    }
    const live = this.readLiveViewportWidth();
    if (live > 0) {
      this.hostWidth$.next(live);
    }
  }

  private readLiveViewportWidth(): number {
    try {
      if (window.top && window.top !== window && window.top.innerWidth) {
        return Math.round(window.top.innerWidth);
      }
    } catch (_crossOrigin) {
    }
    return Math.round(window.innerWidth) || 0;
  }

  private resolveLayoutWidth(
    wrapperW: number,
    hostW: number | null,
    preferHost: boolean
  ): number {
    if (!preferHost) {
      return wrapperW;
    }
    if (hostW && hostW > 0) {
      return hostW;
    }
    return this.readLiveViewportWidth() || window.innerWidth;
  }
}
